import { useState } from 'react';
import { Link } from 'react-router-dom';
import PageMeta from '@/components/feature/PageMeta';
import SiteLayout from '@/components/feature/SiteLayout';
import AboutHero from '@/pages/about/components/AboutHero';
import AboutContact from '@/pages/about/components/AboutContact';
import AboutFeedbackForm from '@/pages/about/components/AboutFeedbackForm';
import { films } from '@/mocks/films';
import { categoryList } from '@/mocks/categories';
import FilmCard from '@/components/feature/FilmCard';
import { getTrending } from '@/lib/catalog';

const scope = [
  {
    icon: 'ri-film-line',
    title: '影视内容聚合',
    text: '整理电影、电视剧与综艺的条目信息，包含类型、地区、年份、简介与演职员等结构化字段，方便按维度检索。',
  },
  {
    icon: 'ri-filter-3-line',
    title: '分类与筛选',
    text: '提供类型、地区、年份三维度组合筛选，并支持更新时间、热度与名称排序，减少翻找成本。',
  },
  {
    icon: 'ri-search-eye-line',
    title: '关键词搜索',
    text: '支持标题模糊匹配，同时覆盖题材、地区与标签，输入一个词就能找到相近的作品。',
  },
  {
    icon: 'ri-heart-3-line',
    title: '本地收藏',
    text: '收藏仅保存在当前浏览器的本地存储中，不上传任何个人数据，清除浏览器数据后收藏即失效。',
  },
];

const faqs = [
  {
    q: '网站可以直接在线播放影视作品吗？',
    a: '目前不能。播放按钮仅为界面交互演示，点击后会给出明确提示，不会跳转外部播放源。',
  },
  {
    q: '页面中的影视作品和演员是真实的吗？',
    a: '不是。为保证合规，站内所有作品名称、海报、演员与评分均为虚构示例数据，仅用于界面展示。',
  },
  {
    q: '收藏的内容会同步到其他设备吗？',
    a: '不会。收藏保存在当前浏览器的本地存储中，换设备或清理浏览器数据后不会保留。',
  },
  {
    q: '如何反馈内容错误或提出建议？',
    a: '可以使用本页的反馈表单（仅前端演示，不产生真实提交），或通过下方公示的邮箱联系我们。',
  },
];

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const trending = getTrending(6);

  return (
    <PageMeta
      title="关于我们 - 91在线视频 - 高清影视在线播放平台"
      description="了解91在线视频的站点定位、业务范围、联系方式与版权免责声明，以及关于影视内容展示与本地收藏功能的说明。"
      keywords="91在线视频,关于我们,网站介绍,版权声明,联系方式"
    >
      <SiteLayout padded>
        <AboutHero />

        <section aria-label="业务范围" className="w-full px-4 md:px-8 pt-14 md:pt-16">
          <div className="max-w-2xl">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2">
              <span className="w-1 h-5 rounded-full bg-accent-500" />
              我们做什么
            </h2>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              91在线视频是一个面向影视爱好者的内容信息门户，专注把「找片」这件事做得更顺手。
              以下是我们提供的核心能力，全部为演示性质的前端功能。
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {scope.map((item) => (
              <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <span className="w-10 h-10 rounded-md bg-primary-500 text-background-50 flex items-center justify-center">
                  <i className={`${item.icon} text-base`} />
                </span>
                <h3 className="mt-4 text-sm font-semibold text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-xs leading-6 text-foreground-600">{item.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section aria-label="站点内容" className="w-full px-4 md:px-8 pt-14 md:pt-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-5 rounded-lg bg-background-100 border border-background-200 p-6">
              <h2 className="text-lg font-bold text-foreground-950">
                <Link to="/" className="hover:text-primary-600 transition-colors">站点内容分布</Link>
              </h2>
              <p className="mt-2 text-xs text-foreground-500">当前演示版本收录的数据概览</p>
              <ul className="mt-5 space-y-3">
                {categoryList.map((item) => (
                  <li key={item.key}>
                    <Link
                      to={item.path}
                      className="flex items-center justify-between gap-3 rounded-md bg-background-50 border border-background-200 px-4 py-3 hover:border-accent-400 transition-colors"
                    >
                      <span className="flex items-center gap-2.5 text-sm text-foreground-900">
                        <span className="w-8 h-8 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
                          <i className={`${item.icon} text-sm`} />
                        </span>
                        {item.label}
                      </span>
                      <span className="text-[11px] text-foreground-500 whitespace-nowrap">{item.accentNote}</span>
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="mt-5 text-[11px] leading-6 text-foreground-400">
                演示数据包含 {films.length} 条精选影视条目，站内最高访问量作品为「{trending[0].title}」。
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="flex items-center justify-between gap-3 mb-4">
                <h2 className="text-lg font-bold text-foreground-950">站内热播示例</h2>
                <Link
                  to="/category/movie"
                  className="text-xs text-foreground-600 hover:text-accent-600 transition-colors whitespace-nowrap flex items-center gap-1"
                >
                  查看更多
                  <i className="ri-arrow-right-line" />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-6">
                {trending.slice(0, 3).map((film, index) => (
                  <FilmCard key={film.id} film={film} index={index} />
                ))}
              </div>
            </div>
          </div>
        </section>

        <section aria-label="常见问题" className="w-full px-4 md:px-8 pt-14 md:pt-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500" />
                常见问题
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                关于播放、数据真实性与收藏机制的说明，点击问题即可展开查看。
              </p>
              <div className="mt-5 rounded-lg bg-secondary-50 border border-secondary-200 p-4">
                <p className="text-xs font-semibold text-secondary-900 flex items-center gap-1.5">
                  <i className="ri-shield-check-line" />
                  合规说明
                </p>
                <p className="mt-2 text-xs leading-6 text-secondary-800">
                  本站在任何情况下都不提供盗版内容、破解播放源或外部下载链接，所有展示内容仅用于产品演示。
                </p>
              </div>
            </div>
            <div className="lg:col-span-7 divide-y divide-background-200 border-y border-background-200">
              {faqs.map((item, index) => (
                <div key={item.q}>
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between gap-3 py-4 text-left cursor-pointer"
                  >
                    <span className="text-sm font-medium text-foreground-950">{item.q}</span>
                    <i
                      className={`${openFaq === index ? 'ri-subtract-line' : 'ri-add-line'} text-foreground-400 shrink-0`}
                    />
                  </button>
                  {openFaq === index && (
                    <p className="pb-4 pr-6 text-xs leading-6 text-foreground-600 animate-fade-up">{item.a}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <AboutContact />

        <section aria-label="反馈" className="w-full px-4 md:px-8 pt-14 md:pt-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-4">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-accent-500" />
                意见反馈
              </h2>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                发现条目信息有误、想补充某部作品，或者对页面体验有建议，都可以告诉我们。
              </p>
              <ul className="mt-5 space-y-2.5 text-xs text-foreground-600">
                {['内容纠错与条目补充建议', '分类、筛选与搜索体验反馈', '产品功能与合作意向沟通'].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-accent-600 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-5 rounded-md bg-accent-50 border border-accent-200 px-3 py-2.5 text-[11px] leading-5 text-accent-900">
                提示：本页反馈表单为前端演示，不会真实发送或存储数据。
              </p>
            </div>
            <div className="lg:col-span-8">
              <AboutFeedbackForm />
            </div>
          </div>
        </section>

        <section aria-label="版权与免责声明" className="w-full px-4 md:px-8 pt-14 md:pt-16">
          <div className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
            <h2 className="text-lg font-bold text-foreground-950">版权与免责声明</h2>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6 text-xs leading-6 text-foreground-600">
              <p>
                1. 本站为影视内容信息展示与交互演示站点，站内所有影视作品名称、海报、评分、演职员等信息均为虚构示例，
                不对应任何真实作品或真实人物。
              </p>
              <p>
                2. 本站不存储、不转码、不提供任何视频文件或播放地址，播放按钮仅作界面交互示意，点击后仅显示功能未开放的提示。
              </p>
              <p>
                3. 站内使用的图片素材均为生成式示意图片，仅用于界面视觉演示，不涉及第三方影视版权素材。
              </p>
              <p>
                4. 用户使用本站过程中产生的收藏等数据仅保存在本地浏览器，不会上传服务器；如需删除，可清除浏览器本地存储。
              </p>
            </div>
          </div>
        </section>
      </SiteLayout>
    </PageMeta>
  );
}